import { useEffect, useState } from "preact/hooks"
import { ExternalLink, Loader2 } from "lucide-react"

import type { AccountingSyncedInvoiceDetail } from "@/api/accounting.tsx"
import { accountingInvoicePdfUrl, getSyncedAccountingInvoice } from "@/api/accounting.tsx"
import { format_date } from "@/accounting/short_pay_display.ts"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { exactCurrencyFormatter } from "@/utils"

/** The synced invoice a short-pay row points at — enough to fetch its detail and PDF. */
export interface ShortPayInvoiceRef {
  accounting_invoice_id: string
  invoice_number: string
}

export interface ShortPayInvoiceDialogProps {
  /** The invoice being inspected; `null` closes the dialog. */
  invoice: ShortPayInvoiceRef | null
  /** Display name of the connected accounting system (e.g. "Xero"). */
  accounting_system?: string
  /** Whether the provider can render the invoice PDF live (Xero / QuickBooks). */
  pdf_available: boolean
  onClose: () => void
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div class="min-w-0">
      <div class="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div class="truncate text-sm text-slate-900" title={value}>
        {value}
      </div>
    </div>
  )
}

/**
 * What the accounting system knows about one synced invoice: the key fields we store from sync,
 * plus the invoice itself rendered as a PDF when the provider can produce one (Xero and QuickBooks
 * can; NetSuite and Sage Intacct expose no PDF endpoint, so those orgs see the fields alone).
 */
export function ShortPayInvoiceDialog({ invoice, accounting_system, pdf_available, onClose }: ShortPayInvoiceDialogProps) {
  const [detail, setDetail] = useState<AccountingSyncedInvoiceDetail | null>(null)
  const [detail_error, setDetailError] = useState<string | null>(null)
  const [pdf_url, setPdfUrl] = useState<string | null>(null)
  const [pdf_error, setPdfError] = useState<string | null>(null)

  const invoice_id = invoice?.accounting_invoice_id

  useEffect(() => {
    if (!invoice_id) {
      return
    }
    let cancelled = false
    setDetail(null)
    setDetailError(null)
    void getSyncedAccountingInvoice(invoice_id).then((res: Awaited<ReturnType<typeof getSyncedAccountingInvoice>>) => {
      if (cancelled) {
        return
      }
      if (res.ok) {
        setDetail(res.value.data)
      } else {
        setDetailError(res.error.message)
      }
    })
    return () => {
      cancelled = true
    }
  }, [invoice_id])

  // The PDF is fetched as a blob (rather than pointing an iframe straight at the endpoint) so a
  // provider error surfaces as a readable message instead of a broken embed.
  useEffect(() => {
    if (!invoice_id || !pdf_available) {
      return
    }
    let cancelled = false
    let object_url: string | null = null
    setPdfUrl(null)
    setPdfError(null)
    void fetch(accountingInvoicePdfUrl(invoice_id), { credentials: "same-origin" })
      .then(async (res: Response) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}) as { message?: string })
          throw new Error(body.message ?? `The accounting system returned an error (HTTP ${res.status}).`)
        }
        // A misrouted request (e.g. the SPA fallback) can answer 200 with HTML — embedding that
        // as a "PDF" renders garbage, so only accept what is actually a PDF.
        if (!(res.headers.get("content-type") ?? "").includes("pdf")) {
          throw new Error("The response was not a PDF.")
        }
        const blob = await res.blob()
        if (cancelled) {
          return
        }
        object_url = URL.createObjectURL(blob)
        setPdfUrl(object_url)
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setPdfError(e.message)
        }
      })
    return () => {
      cancelled = true
      if (object_url) {
        URL.revokeObjectURL(object_url)
      }
    }
  }, [invoice_id, pdf_available])

  const system = accounting_system ?? "the accounting system"

  return (
    <Dialog open={!!invoice} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent class="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invoice {invoice?.invoice_number}</DialogTitle>
          <DialogDescription>As synced from {system}.</DialogDescription>
        </DialogHeader>

        {detail_error && (
          <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{detail_error}</div>
        )}

        {!detail && !detail_error && (
          <div class="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading invoice…
          </div>
        )}

        {detail && (
          <div class="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3 sm:grid-cols-4">
            <Fact label="Customer" value={detail.customer_name ?? "—"} />
            <Fact label="Invoice date" value={format_date(detail.invoice_date)} />
            <Fact label="Due date" value={format_date(detail.due_date)} />
            <Fact label="Status" value={detail.status ?? "—"} />
            <Fact label="Total" value={exactCurrencyFormatter(Number(detail.total_amount))} />
            <Fact
              label="Amount due"
              value={detail.amount_due === undefined || detail.amount_due === null ? "—" : exactCurrencyFormatter(Number(detail.amount_due))}
            />
            <Fact label="Reference" value={detail.description ?? "—"} />
            <Fact label="Last synced" value={format_date(detail.updated_at)} />
          </div>
        )}

        {pdf_available ? (
          <div class="space-y-2">
            {pdf_url ? (
              <>
                <iframe src={pdf_url} title={`Invoice ${invoice?.invoice_number} PDF`} class="h-[60vh] w-full rounded-md border border-slate-200" />
                <a
                  href={pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="h-3.5 w-3.5" /> Open PDF in a new tab
                </a>
              </>
            ) : pdf_error ? (
              <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Couldn't fetch the invoice PDF from {system}: {pdf_error}
              </div>
            ) : (
              <div class="flex items-center gap-2 py-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Fetching the PDF from {system}…
              </div>
            )}
          </div>
        ) : (
          <div class="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {system} doesn't provide invoice PDFs over its API, so only the synced fields are shown here.
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
